import {
  useLocationPathname,
  useLocationSearch,
} from '@rocketlane/rocket-router';
import {
  MutationFunction,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import dotPropImmutable from 'dot-prop-immutable';
import { Dictionary, keyBy } from 'lodash';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Account,
  AccountAPI,
  AccountInitDataProjectsQuery,
  AccountInitialData,
  AccountMeta,
  CustomDomain,
  EmailConfig,
  FtuePayload,
  UpdateAccountPayload,
} from 'api/account';
import {
  CompanyAPI,
  CompanyLogoResponse,
  CreateCompanyPayload,
  UpdateCompanyPayload,
} from 'api/company';
import { DocumentsAPI } from 'api/document/document-core';
import { DocumentSourceType } from 'api/document/types/document';
import {
  FieldSectionSubType,
  FieldSectionType,
} from 'api/field-sections/types';
import { FieldPayload, FieldsAPI } from 'api/fields';
import {
  AccountFields,
  FieldSectionSubTypeForApi,
  FieldsObjectTypeForApi,
} from 'api/fields/fields';
import { HomeAPI, HomeResponse } from 'api/home';
import {
  Company,
  CompanyAddress,
  OutOfOfficeConfig,
  ProjectId,
  ProjectPhaseId,
  User,
  UserType,
} from 'api/models';
import { PhaseAPI, UpdateEntityOrderPayload } from 'api/phase';
import {
  MembersMeta,
  Project,
  ProjectAPI,
  ProjectForRm,
  ProjectMergePayload,
  ProjectPhase,
  ProjectToTemplatePayload,
  ProjectWithFinancialContractType,
  ProjectWithPartners,
  ProjectWithStatus,
} from 'api/project';
import { ObjectType } from 'api/pushes';
import { Space, SpaceAPI } from 'api/space';
import { SprintPlanningAPI } from 'api/sprint-planning';
import { Epic } from 'api/sprint-planning/types';
import {
  DuplicateTaskProps,
  Field,
  FieldsWithUniqueValuesForBulkEditor,
  Task,
  TaskAPI,
  UpdateSubTaskOrderPayload,
} from 'api/tasks';
import type { GroupedFilter } from 'api/tasks/filter-types';
import {
  AvatarResponse,
  BulkUserCreatePayload,
  UserAPI,
  UserProfileInfo,
} from 'api/user';
import { toastService } from 'components/molecules/toast';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { UserCreationProps } from 'contexts/project-creation';
import { RootState } from 'data';
import { success } from 'data/current-user-profile';
import { InvitedMembers } from 'data/members/slice';
import { Template, TemplatesAPI } from 'data/templates/api';
import {
  ProjectPhaseOption,
  ProjectTemplatesAPI,
} from 'data/templates/projects/api';
import { ProjectTemplateApi } from 'data/templates/projects/types';
import { useBudgetFeature } from 'hooks/budgets/use-budget-feature';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useFilterPointsFieldsFromTaskFields } from 'hooks/use-filter-point-fields-from-task-fields';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import { useHasAccessToSkillMatrix } from 'hooks/use-skill-matrix-permission';
import { invalidateSwrUser } from 'hooks/use-swr-user-hooks';
import { useTranslation } from 'hooks/use-translation';
import { useUser, useUserSafe } from 'hooks/use-user';
import { added } from 'stores/companies';
import { FieldsStore, putAll } from 'stores/fields';
import { SETTINGS_SECTION_FIELDS_QUERY_KEY } from 'utils/project-fields/use-settings-section-fields';
import { migrations } from 'workers/migrations';
import {
  ACCOUNT_META_DATA_QUERY_KEY,
  useAccountConfigQuery,
  useProjectsFromInitData,
} from './account';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
  getProjectUsersLiteKey,
} from './common';
import {
  ALL_PROJECT_CURRENT_PHASES_QUERY_KEY,
  ALL_PROJECTS_QUERY_KEY,
  ALL_PROJECTS_RM_QUERY_KEY,
  ARCHIVED_PROJECTS_QUERY_KEY,
  FINANCIAL_FIELDS_QUERY_KEY,
  fiveMinutes,
  HOME_QUERY_KEY,
  oneDay,
  oneHour,
  PROJECT_STATUS_FIELD_QUERY_KEY,
  PROJECTS_META_QUERY_KEY,
  tenMinutes,
  SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY,
  TASK_FIELDS_QUERY_KEY,
  DELETED_PROJECTS_QUERY_KEY,
  CSAT_APPROVAL_COUNT,
  ALL_EPICS_QUERY_KEY,
  ALL_APPROVALS_HOME_QUERY_KEY,
  TIME_ENTRY_FIELDS_QUERY_KEY,
  TEAM_MEMBER_FIELDS_QUERY_KEY,
  PROJECT_FIELDS_QUERY_KEY,
  COMPANY_FIELDS_QUERY_KEY,
  INTERVALS_FIELDS_QUERY_KEY,
  KEY_EVENTS_FIELDS_QUERY_KEY,
  fieldsQueryKeys,
  EPIC_FIELDS_QUERY_KEY,
  PROJECT_PHASE_FIELDS_QUERY_KEY,
  INVOICE_FIELDS_QUERY_KEY,
  USERS_FIELDS_QUERY_KEY,
  CUSTOMER_TEAM_MEMBER_FIELDS_QUERY_KEY,
  EXPENSE_FIELDS_QUERY_KEY,
  USERS_SKILLS_FIELDS_QUERY_KEY,
  ALLOCATION_FIELDS_QUERY_KEY,
  ALL_FIELDS_QUERY_KEY,
  // CREDIT_NOTE_FIELDS_QUERY_KEY,
} from './constants';
import {
  COMPANY_FIELD_SECTIONS_QUERY_KEY,
  PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW as PROJECT_FIELD_SECTIONS_QUERY_KEY,
  SPRINT_FIELD_SECTIONS_QUERY_KEY,
  SKILL_FIELD_SECTIONS_QUERY_KEY,
  TASK_FIELD_SECTIONS_QUERY_KEY,
  TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY,
  CUSTOMER_TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY,
  INVOICE_FIELD_SECTIONS_QUERY_KEY,
  BUDGET_FINANCIAL_FIELD_SECTIONS_QUERY_KEY,
  EPIC_FIELD_SECTIONS_QUERY_KEY,
  ALLOCATION_FIELD_SECTIONS_QUERY_KEY,
  TIME_ENTRY_FIELD_SECTIONS_QUERY_KEY,
} from './field-sections';
import {
  useAllFieldsBaseQuery,
  useAllFieldsQueryV2,
  useTaskFieldsBaseQuery,
} from './fields';
import {
  ALL_MEMBERS_QUERY_KEY,
  ALL_USERS_QUERY_KEY,
  MEMBERS_V2_MODIFIED_QUERY_KEY,
  MEMBERS_V2_QUERY_KEY,
} from './members';
import { useHideNonStrategicPartner } from './partner';
import { PROJECT_TEMPLATE_OPTIONS_QUERY_KEY } from './templates/project-templates';

export const TEMPLATES_QUERY_KEY = 'templates';

export const getTemplatesQueryKey = (): string[] => [TEMPLATES_QUERY_KEY];
export const getStatusUpdateTemplatesQueryKey = (): string[] => [
  'templates',
  'status-update',
];
export const getTemplatesDetailsQueryKey = (
  templateId: string | number,
): (string | number)[] => {
  return [...getTemplatesQueryKey(), templateId];
};
export const getTemplatesPublishQueryKey = (
  templateId: string | number,
): (string | number)[] => {
  return [...getTemplatesDetailsQueryKey(templateId), 'publish'];
};
export const getTaskTemplatesQueryKey = (): string[] => ['templates', 'task'];

export const useTemplatesQuery = (
  queryConfig: UseQueryOptions<Template[], unknown, Template[], string[]> = {},
) =>
  useQuery(getTemplatesQueryKey(), () => TemplatesAPI.getAllTemplates(), {
    staleTime: oneDay,
    ...queryConfig,
  });

export const useUserCreatedTaskTemplatesQuery = () => {
  const isCurrentUserPartner = useIsCurrentUserPartner();

  return useQuery(
    getTaskTemplatesQueryKey(),
    () => TemplatesAPI.getUserCreatedTaskTemplates(),
    { enabled: !isCurrentUserPartner },
  );
};

export const useDeleteTemplateMutation = (): UseMutationResult<
  number,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation<number, unknown, number>(
    (id) => {
      return TemplatesAPI.deleteById(id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getTemplatesQueryKey());
        await queryClient.invalidateQueries(PROJECT_TEMPLATE_OPTIONS_QUERY_KEY);
      },
    },
  );
};

export const useDuplicateTemplateMutation = (): UseMutationResult<
  Template,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (id) => {
      return TemplatesAPI.cloneById(id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getTemplatesQueryKey());
      },
    },
  );
};

export const getProjectMembersQueryKey = ([, projectId]: ProjectId): [
  string,
  number,
] => ['squad', projectId];

export const useProjectMembersQuery = (projectId: ProjectId, options = {}) =>
  useQuery(
    getProjectMembersQueryKey(projectId),
    () => ProjectAPI.getMembers(projectId[1]),
    {
      staleTime: oneDay,
      ...options,
    },
  );

export const getProjectSpacesQueryKey = ([, projectId]: ProjectId): [
  string,
  number,
] => ['spaces', projectId];

export const getProjectPhaseQueryKey = ([, phaseId]: ProjectPhaseId): [
  string,
  number,
] => ['phase', phaseId];

export const useProjectSpacesQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<Space[], unknown, Space[], [string, number]> = {},
) => {
  return useQuery({
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    ...options,
    queryKey: getProjectSpacesQueryKey(projectId),
    queryFn: () => SpaceAPI.getSpaces(projectId[1]),
  });
};

export const SpaceDetailQueryKey = (spaceId: number): [string, number] => [
  'space',
  spaceId,
];

export const useSpaceDetailQuery = (
  spaceId: number,
  {
    queryKey,
    queryFn,
    ...rest
  }: UseQueryOptions<Space, unknown, Space, [string, number]> = {},
) =>
  useQuery(
    SpaceDetailQueryKey(spaceId),
    () => SpaceAPI.getSpaceDetail(spaceId),
    {
      staleTime: oneHour,
      ...rest,
    },
  );

export const useProjectsQuery = (
  options: UseQueryOptions<
    Project[],
    unknown,
    Project[],
    typeof ALL_PROJECTS_QUERY_KEY
  > = {},
): UseQueryResult<Project[]> => {
  const { isDependentEntitiesLoading } = useHideNonStrategicPartner();

  return useQuery(ALL_PROJECTS_QUERY_KEY, () => ProjectAPI.getProjectsLight(), {
    enabled: !isDependentEntitiesLoading,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  });
};

export const useProjectsQueryLookup = () => {
  const { data: projects } = useProjectsQuery();

  const lookup = useMemo(() => keyBy(projects ?? [], 'projectId'), [projects]);

  return lookup;
};

export const useProjectsForRmQuery = (
  options: UseQueryOptions<
    ProjectForRm[],
    unknown,
    ProjectForRm[],
    typeof ALL_PROJECTS_RM_QUERY_KEY
  > = {},
): UseQueryResult<ProjectForRm[]> => {
  const { isDependentEntitiesLoading } = useHideNonStrategicPartner();

  return useQuery(
    ALL_PROJECTS_RM_QUERY_KEY,
    () => ProjectAPI.getProjectsLightForRM(),
    {
      enabled: !isDependentEntitiesLoading,
      staleTime: oneHour,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...options,
    },
  );
};

export const ALL_PROJECT_STATUS_MAPPING_QUERY_KEY = [
  'all-project-status-mapping',
];

// central place for the query key
export const PROJECT_STATUS_MAPPING_QUERY_KEY = (projectIds?: string) => [
  ALL_PROJECT_STATUS_MAPPING_QUERY_KEY,
  projectIds ?? '',
];

export const useProjectStatusMappingQuery = (
  projectIds?: string,
): UseQueryResult<Dictionary<ProjectWithStatus>> => {
  return useQuery(
    PROJECT_STATUS_MAPPING_QUERY_KEY(projectIds),
    () => ProjectAPI.getProjectStatusMapping(projectIds ?? ''),
    {
      staleTime: GENERAL_API_STALE,
    },
  );
};

export const ALL_PROJECT_FINANCIAL_CONTRACT_MAPPING_QUERY_KEY = [
  'all-project-project-financial-mapping',
];

export const useProjectFinancailContractMappingQuery = (): UseQueryResult<
  Dictionary<ProjectWithFinancialContractType>
> => {
  const isMultiLineBudgetsEnabled = useBudgetFeature();

  return useQuery(
    ALL_PROJECT_FINANCIAL_CONTRACT_MAPPING_QUERY_KEY,
    () => ProjectAPI.getProjectFinancialContractMapping(),
    { enabled: !isMultiLineBudgetsEnabled },
  );
};

export const PROJECT_PARTNER_MAPPING_QUERY_KEY = (projectIds: number[]) => {
  const projectIdsString = projectIds.join(',');

  return ['project-partner-mapping', projectIdsString];
};

export const useProjectPartnersMappingQuery = (
  projectIds: number[],
): UseQueryResult<Dictionary<ProjectWithPartners>> => {
  return useQuery(
    PROJECT_PARTNER_MAPPING_QUERY_KEY(projectIds),
    () => ProjectAPI.getProjectPartnerMapping(projectIds),
    { enabled: projectIds.length > 0 },
  );
};

export const PROJECT_PHASES_QUERY_KEY = (projectId: number) => [
  `${projectId}_phases`,
];

export const useProjectsPhasesQuery = (
  projectId: number,
  enabled: boolean,
): UseQueryResult<ProjectPhase[]> => {
  return useQuery(
    PROJECT_PHASES_QUERY_KEY(projectId),
    () => ProjectAPI.getPhasesForAProject(projectId),
    { enabled: projectId != null && enabled },
  );
};

export const useGetAllProjectCurrentPhases = (
  options: UseQueryOptions<
    ProjectPhase[],
    unknown,
    ProjectPhase[],
    typeof ALL_PROJECT_CURRENT_PHASES_QUERY_KEY
  > = {},
): UseQueryResult<ProjectPhase[]> => {
  return useQuery(
    ALL_PROJECT_CURRENT_PHASES_QUERY_KEY,
    () => ProjectAPI.getAllProjectCurrentPhases(),
    {
      staleTime: oneHour,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...options,
    },
  );
};

export const useProjectsMetaQuery = (
  options: UseQueryOptions<
    Project[],
    unknown,
    Project[],
    typeof PROJECTS_META_QUERY_KEY
  > = {},
): UseQueryResult<Project[]> => {
  return useQuery(PROJECTS_META_QUERY_KEY, () => ProjectAPI.getProjectsMeta(), {
    staleTime: tenMinutes,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...options,
  });
};

export const useCreateProjectToTemplateMutation = (
  projectId: number,
  options: UseMutationOptions<
    ProjectTemplateApi,
    unknown,
    ProjectToTemplatePayload
  > = {},
): UseMutationResult<ProjectTemplateApi, unknown, ProjectToTemplatePayload> => {
  return useMutation(
    async (payload: ProjectToTemplatePayload) => {
      const template = await ProjectAPI.createProjectToTemplate(
        projectId,
        payload,
      );

      return migrations.addEntityOrderToProjectTemplatePhases(template);
    },
    {
      ...options,
    },
  );
};

export const useArchivedProjectsQuery = (
  options: UseQueryOptions<
    Project[],
    unknown,
    Project[],
    typeof ARCHIVED_PROJECTS_QUERY_KEY
  > = {},
): UseQueryResult<Project[]> => {
  return useQuery(
    ARCHIVED_PROJECTS_QUERY_KEY,
    () => ProjectAPI.getArchivedProjectList(),
    {
      staleTime: tenMinutes,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useDeletedProjectsQuery = (
  options: UseQueryOptions<
    Project[],
    unknown,
    Project[],
    typeof DELETED_PROJECTS_QUERY_KEY
  > = {},
): UseQueryResult<Project[]> => {
  return useQuery(
    DELETED_PROJECTS_QUERY_KEY,
    () => ProjectAPI.getDeletedProjectList(),
    {
      staleTime: tenMinutes,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useCombinedProjectsData = () => {
  const { data: projects } = useProjectsFromInitData();
  const { data: archivedProjects } = useArchivedProjectsQuery();
  const { data: deletedProjects } = useDeletedProjectsQuery();

  const combinedProjectsData: AccountInitDataProjectsQuery = useMemo(() => {
    const projectsArray = projects?.projects ?? [];
    const archivedProjectsArray = archivedProjects || [];
    const deletedProjectsArray = deletedProjects || [];

    return {
      projects: [
        ...projectsArray,
        ...archivedProjectsArray,
        ...deletedProjectsArray,
      ],
      byId: {
        ...projects?.byId,
        ...keyBy(archivedProjectsArray, 'projectId'),
        ...keyBy(deletedProjectsArray, 'projectId'),
      },
    };
  }, [deletedProjects, archivedProjects, projects]);

  return combinedProjectsData;
};

export const useCompanyTasksQuery = () => {
  return useQuery(['company-tasks'], () => HomeAPI.getMyCompanyTasks(), {
    staleTime: tenMinutes,
  });
};

export const useCsatAndApprovalCount = (companyIds: string | undefined) => {
  return useQuery(
    CSAT_APPROVAL_COUNT,
    () => HomeAPI.getCsatAndApprovalsCount(companyIds),
    {
      enabled: Boolean(companyIds?.length),
    },
  );
};

export const useAllApprovalsQuery = () => {
  return useQuery(
    ALL_APPROVALS_HOME_QUERY_KEY,
    () => HomeAPI.getAllApprovals(),
    {
      staleTime: tenMinutes,
    },
  );
};

export const useHomeQuery = (
  options: UseQueryOptions<
    HomeResponse,
    unknown,
    HomeResponse,
    typeof HOME_QUERY_KEY
  > = {},
): UseQueryResult<HomeResponse> => {
  return useQuery(HOME_QUERY_KEY, () => HomeAPI.getHomeScreenResponse(), {
    staleTime: tenMinutes,
    retry: 2,
    refetchOnMount: 'always',
    ...options,
  });
};

export const useTaskFieldsQuery = (
  queryOptions: UseQueryOptions<
    Field[],
    unknown,
    Field[],
    typeof TASK_FIELDS_QUERY_KEY
  > = {},
) => {
  return useTaskFieldsBaseQuery({
    staleTime: oneDay,
    ...queryOptions,
  });
};

export const useAllFieldsQuery = (queryOptions = {}) => {
  const dispatch = useDispatch();
  const { filterPointsFieldsFromTaskFields } =
    useFilterPointsFieldsFromTaskFields();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);

  return useAllFieldsBaseQuery({
    select: (res) => filterPointsFieldsFromTaskFields(res) as Field[],
    ...queryOptions,
    onSuccess: (data) => {
      if (FieldsStore.loaded(fieldsStore)) {
        return;
      }
      dispatch(putAll(data));
    },
  });
};

// export const useFieldsQueryFilter = (
//   select: ((f: Field) => boolean)[],
// ): UseQueryResult<Field[], unknown> => {
//   const { filterPointsFieldsFromTaskFields } =
//     useFilterPointsFieldsFromTaskFields();

//   return useAllFieldsBaseQuery({
//     select: useCallback(
//       (fields: any) => {
//         return filterPointsFieldsFromTaskFields(
//           fields.filter((field) => select.every((filter) => filter(field))),
//         ) as Field[];
//       },
//       [select, filterPointsFieldsFromTaskFields],
//     ),
//   });
// };

// export const useCustomTaskFields = (
//   customTaskFieldFilter: Array<(field: Field) => boolean>,
// ): UseQueryResult<Field[], unknown> =>
//   useFieldsQueryFilter(customTaskFieldFilter);

type CustomFieldsValueForBulkUpdateKey = [
  'custom-field-unique-values',
  number[],
];

type FieldUniqueValuesForBulkEditorKey = ['field-unique-values', number[]];

export const getCustomFieldUniqueValuesQueryKey = (
  ids: number[],
): CustomFieldsValueForBulkUpdateKey => {
  return ['custom-field-unique-values', ids] as const;
};

export const useCustomTaskFieldValuesForBulkEditor = (
  ids: number[],
  shouldFetchCustomFields: boolean,
  options?: UseQueryOptions<
    FieldsWithUniqueValuesForBulkEditor[],
    unknown,
    FieldsWithUniqueValuesForBulkEditor[],
    CustomFieldsValueForBulkUpdateKey
  >,
): UseQueryResult<FieldsWithUniqueValuesForBulkEditor[]> => {
  return useQuery(
    getCustomFieldUniqueValuesQueryKey(ids),
    () => TaskAPI.getTaskFieldValues(ids),
    {
      enabled: shouldFetchCustomFields && ids.length > 0,
      refetchOnMount: true,
      ...options,
    },
  );
};

export const getFieldUniqueValuesQueryKey = (
  ids: number[],
): FieldUniqueValuesForBulkEditorKey => {
  return ['field-unique-values', ids] as const;
};

export const useProjectFieldValuesForBulkEditor = (
  ids: number[],
  options?: UseQueryOptions<
    FieldsWithUniqueValuesForBulkEditor[],
    unknown,
    FieldsWithUniqueValuesForBulkEditor[],
    FieldUniqueValuesForBulkEditorKey
  >,
): UseQueryResult<FieldsWithUniqueValuesForBulkEditor[]> => {
  return useQuery(
    getFieldUniqueValuesQueryKey(ids),
    () => ProjectAPI.getProjectFieldValues(ids),
    {
      enabled: ids.length > 0,
      refetchOnMount: true,
      ...options,
    },
  );
};

type QueryOptionTypeForProjectFields = UseQueryOptions<
  AccountFields,
  unknown,
  AccountFields,
  typeof PROJECT_FIELDS_QUERY_KEY
>;

export const useProjectFieldsQueryV2 = ({
  queryKey,
  queryFn,
  ...queryOptions
}: QueryOptionTypeForProjectFields = {}) => {
  return useQuery({
    ...queryOptions,
    queryKey: PROJECT_FIELDS_QUERY_KEY,
    queryFn: () =>
      FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Projects),
  });
};

export const useKeyEventsFieldsQueryV2 = () => {
  return useQuery({
    queryKey: KEY_EVENTS_FIELDS_QUERY_KEY,
    queryFn: () =>
      FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.KeyEvents,
        undefined,
        true,
      ),
  });
};

export const useIntervalsFieldsQueryV2 = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: INTERVALS_FIELDS_QUERY_KEY,
    queryFn: () =>
      FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Intervals,
        undefined,
        true,
      ),
    enabled,
  });
};

// export const useProjectFieldsQuery = () => {
//   const [isInvoiceFeatureEnabled] = useAccountFeature(AccountFeature.Invoices);

//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, objectSubType }) =>
//           objectType === FieldSectionType.Project &&
//           (isInvoiceFeatureEnabled ||
//             objectSubType !== FieldSectionSubType.InvoiceProject),
//       );
//     },
//   });
// };

export const useProjectFieldsQuery = () => {
  const [isInvoiceFeatureEnabled] = useAccountFeature(AccountFeature.Invoices);

  return useQuery({
    queryKey: PROJECT_FIELDS_QUERY_KEY,
    queryFn: async () => {
      return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Projects);
    },
    staleTime: oneDay,
    select: (res) => {
      return (res?.fields || []).filter(
        ({ objectSubType }) =>
          isInvoiceFeatureEnabled ||
          objectSubType !== FieldSectionSubType.InvoiceProject,
      );
    },
  });
};

export const useProjectStatusFieldQueryV2 = () => {
  const { data: config } = useAccountConfigQuery();

  return useMemo(
    () =>
      config?.fields?.find(
        (f) =>
          f.fieldName === 'Status' && f.objectType === FieldSectionType.Project,
      ),
    [config],
  );
};

// export const useReportFormulaFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, fieldType }) =>
//           objectType.toString() === 'REPORTS' && fieldType === 'FORMULA',
//       );
//     },
//   });

export const useReportFormulaFieldsQuery = () =>
  useAllFieldsBaseQuery({
    staleTime: oneDay,
    select: (res) => {
      return res.filter(
        ({ objectType, fieldType }) =>
          objectType.toString() === 'REPORTS' && fieldType === 'FORMULA',
      );
    },
  });

export const useProjectStatusFieldQuery = () => {
  const { data: projectFields, isLoading } = useProjectFieldsQuery();

  return useQuery(
    PROJECT_STATUS_FIELD_QUERY_KEY,
    () => {
      if (!projectFields) {
        return undefined;
      }

      return projectFields.find((field) => field.fieldName === 'Status');
    },
    {
      enabled: !isLoading && !!projectFields,
    },
  );
};
// export const useEnabledProjectFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, isEnabled: isFieldEnabled }) =>
//           objectType === FieldSectionType.Project && isFieldEnabled,
//       );
//     },
//   });
export const useEnabledProjectFieldsQuery = () => {
  const { data } = useProjectFieldsQueryV2();

  return (data?.fields ?? []).filter(({ isEnabled }) => isEnabled);
};

// export const useTasksFieldsQuery = () => {
//   const { filterPointsFieldsFromTaskFields } =
//     useFilterPointsFieldsFromTaskFields();

//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return filterPointsFieldsFromTaskFields(
//         res.filter(({ objectType }) => objectType === FieldSectionType.Task),
//       ) as Field[];
//     },
//   });
// };

// export const useEnabledTaskFieldsQuery = () => {
//   const { filterPointsFieldsFromTaskFields } =
//     useFilterPointsFieldsFromTaskFields();

//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return filterPointsFieldsFromTaskFields(
//         res.filter(
//           ({ objectType, isEnabled: isFieldEnabled }) =>
//             objectType === FieldSectionType.Task && isFieldEnabled,
//         ),
//       ) as Field[];
//     },
//   });
// };

export const useEnabledTaskFieldsQuery = () => {
  const { filterPointsFieldsFromTaskFields } =
    useFilterPointsFieldsFromTaskFields();
  const { data } = useTaskFieldsBaseQuery();

  return filterPointsFieldsFromTaskFields(
    (data ?? []).filter(({ isEnabled }) => isEnabled),
  );
};

export const useTeamMemberFieldsQuery = () =>
  useQuery({
    queryKey: TEAM_MEMBER_FIELDS_QUERY_KEY,
    queryFn: async () => {
      return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Users);
    },
    staleTime: oneDay,
    select: (data) => data?.fields ?? [],
  });

// export const useInheritableTimeEntryFields = () => {
//   const { filterPointsFieldsFromTaskFields } =
//     useFilterPointsFieldsFromTaskFields();

//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       const filteredFields = res.filter(
//         ({ objectType, isEnabled }) =>
//           [
//             FieldSectionType.Project,
//             FieldSectionType.Task,
//             FieldSectionType.Users,
//             FieldSectionType.Company,
//           ].includes(objectType) && isEnabled,
//       );

//       // Transform projectOwner field's fieldDataType from NUMBER to USER
//       const transformedFields = filteredFields.map((field) => {
//         if (
//           field.fieldName === 'projectOwner' &&
//           field.objectType === FieldSectionType.Project &&
//           field.fieldDataType === 'NUMBER'
//         ) {
//           return {
//             ...field,
//             fieldDataType: 'USER',
//           };
//         }

//         return field;
//       });

//       return filterPointsFieldsFromTaskFields(transformedFields) as Field[];
//     },
//   });
// };

export const useInheritableTimeEntryFieldsV2 = () => {
  const { filterPointsFieldsFromTaskFields } =
    useFilterPointsFieldsFromTaskFields();

  const projectFields = useAllFieldsQueryV2(
    { objectType: FieldSectionType.Project },
    { staleTime: oneDay },
  );
  const taskFields = useTaskFieldsBaseQuery({ staleTime: oneDay });
  const usersFields = useAllFieldsQueryV2(
    { objectType: FieldSectionType.Users },
    { staleTime: oneDay },
  );
  const companyFields = useAllFieldsQueryV2(
    { objectType: FieldSectionType.Company },
    { staleTime: oneDay },
  );

  const queries = [projectFields, taskFields, usersFields, companyFields];
  const isLoading = queries.some((query) => query.isLoading);

  const data = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    const enabledFields = queries
      .flatMap((q) => q.data ?? [])
      .filter((f) => f.isEnabled);

    // Transform projectOwner field's fieldDataType from NUMBER to USER
    const transformedFields = enabledFields.map((field) => {
      if (
        field.fieldName === 'projectOwner' &&
        field.objectType === FieldSectionType.Project &&
        field.fieldDataType === 'NUMBER'
      ) {
        return { ...field, fieldDataType: 'USER' };
      }

      return field;
    });

    return filterPointsFieldsFromTaskFields(transformedFields) as Field[];
  }, [
    isLoading,
    projectFields.data,
    taskFields.data,
    usersFields.data,
    companyFields.data,
  ]);

  return { data, isLoading };
};

// export const useTimeEntryFieldsQuery = () => {
//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(({ objectType, objectSubType }) => {
//         return (
//           objectType === FieldSectionType.TimeEntry &&
//           objectSubType === FieldSectionSubType.Default
//         );
//       });
//     },
//   });
// };

// export const useTimeEntryFieldsQueryV2 = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(({ objectType, objectSubType }) => {
//         return (
//           objectType === FieldSectionType.TimeEntry &&
//           objectSubType === FieldSectionSubType.Default
//         );
//       });
//     },
//   });

export const useTimeEntryCustomFieldsQueryV2 = () => {
  return useQuery({
    queryKey: TIME_ENTRY_FIELDS_QUERY_KEY,
    queryFn: async () => {
      return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.TimeEntry);
    },
    staleTime: oneDay,
    select: (data) => data?.fields ?? [],
  });
};

export const useExpenseFieldsQuery = () =>
  useQuery({
    queryKey: EXPENSE_FIELDS_QUERY_KEY,
    queryFn: async () => {
      const resp = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Expense,
      );

      return resp?.fields || [];
    },
    staleTime: oneDay,
  });

export const useAllocationFieldsQuery = () =>
  useQuery({
    queryKey: ALLOCATION_FIELDS_QUERY_KEY,
    queryFn: () =>
      FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.ResourceAllocation,
      ),
    select: (data) => data?.fields ?? [],
    staleTime: oneDay,
  });
// export const useExpenseFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(({ objectType, objectSubType }) => {
//         return (
//           objectType === FieldSectionType.Expense &&
//           objectSubType === FieldSectionSubType.Default
//         );
//       });
//     },
//   });

// export const useCustomerTeamMemberFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, objectSubType }) =>
//           objectType === FieldSectionType.Users &&
//           objectSubType === FieldSectionSubType.ExternalUser,
//       );
//     },
//   });

export const useCustomerTeamMemberFieldsQuery = () =>
  useQuery({
    queryKey: CUSTOMER_TEAM_MEMBER_FIELDS_QUERY_KEY,
    queryFn: async () => {
      const resp = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Users,
        FieldSectionSubTypeForApi.ExternalUser,
      );

      return resp?.fields || [];
    },
    staleTime: oneDay,
  });

// export const useTeamMemberUserFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({
//           objectType,
//           objectSubType,
//           fieldDataType,
//           isEnabled: isFieldEnabled,
//         }) =>
//           objectType === FieldSectionType.Users &&
//           objectSubType === FieldSectionSubType.Default &&
//           fieldDataType === FieldDataType.User &&
//           isFieldEnabled,
//       );
//     },
//   });

export const useTeamMemberUserFieldsQuery = () => {
  return useQuery({
    queryKey: USERS_FIELDS_QUERY_KEY,
    queryFn: async () => {
      const resp = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Users,
      );

      return resp?.fields || [];
    },
    select: (resp) =>
      resp.filter(
        ({ fieldDataType, isEnabled }) =>
          fieldDataType === FieldDataType.User && isEnabled,
      ),
    staleTime: oneDay,
  });
};

const useSkillFieldsQueryForReadAccess = (resource?: string) => {
  const hasAccessToSkillMatrix = useHasAccessToSkillMatrix();

  return useQuery(
    [SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY, resource],
    () => FieldsAPI.getSkillFieldIdsForReadAccess(resource),
    {
      enabled: hasAccessToSkillMatrix,
    },
  );
};

export const useSkillFieldsQuery = (resource?: string) => {
  const hasAccessToSkillMatrix = useHasAccessToSkillMatrix();
  const { data: skillFieldIds } = useSkillFieldsQueryForReadAccess(resource);

  return useQuery({
    queryKey: USERS_SKILLS_FIELDS_QUERY_KEY,
    queryFn: async () => {
      return FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Users,
        FieldSectionSubTypeForApi.Skill,
      );
    },
    staleTime: oneDay,
    select: (data) => {
      return data?.fields.filter(
        ({ fieldId }) =>
          hasAccessToSkillMatrix && skillFieldIds?.includes(fieldId),
      );
    },
  });
};

// export const useInvoiceFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType }) => objectType === FieldSectionType.Invoice,
//       );
//     },
//   });
export const useInvoiceFieldsQuery = () => {
  return useQuery({
    queryKey: INVOICE_FIELDS_QUERY_KEY,
    queryFn: async () => {
      const resp = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Invoice,
      );

      return resp?.fields || [];
    },
    staleTime: oneDay,
  });
};

// export const useCreditNoteFieldsQuery = () =>
//   useQuery({
//     queryKey: CREDIT_NOTE_FIELDS_QUERY_KEY,
//     queryFn: async () => {
//       const resp = await FieldsAPI.getFieldsByObjectType(
//         FieldsObjectTypeForApi.CreditNote,
//       );

//       return resp?.fields || [];
//     },
//     staleTime: oneDay,
//   });

// TODO: Remove this once the API is updated
export const useCreditNoteFieldsQuery = () =>
  useAllFieldsBaseQuery({
    staleTime: oneDay,
    select: (res) => {
      return res.filter(
        ({ objectType }) => objectType === FieldSectionType.CreditNote,
      );
    },
  });

// export const useCompanyFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType }) => objectType === FieldSectionType.Company,
//       );
//     },
//   });

export const useCompanyFieldsQuery = () => {
  return useQuery({
    queryKey: COMPANY_FIELDS_QUERY_KEY,
    queryFn: async () => {
      return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Company);
    },
    staleTime: oneDay,
    select: (data) => data?.fields ?? [],
  });
};

// export const useEnabledCompanyFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, isEnabled: isFieldEnabled }) =>
//           isFieldEnabled,
//       );
//     },
//   });

export const useEnabledCompanyFieldsQuery = () => {
  const { data } = useCompanyFieldsQuery();

  return (data ?? []).filter(({ isEnabled }) => isEnabled);
};

// export const useProjectPhaseFields = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (response) => {
//       return response.filter(
//         ({ objectType }) => objectType === FieldSectionType.ProjectPhase,
//       );
//     },
//   });

export const useProjectPhaseFields = () => {
  return useQuery({
    queryKey: PROJECT_PHASE_FIELDS_QUERY_KEY,
    queryFn: async () => {
      const projectPhaseFields = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.ProjectPhase,
      );

      return projectPhaseFields?.fields || [];
    },
    cacheTime: 0,
  });
};

export const useFinancialFieldsQuery = (
  queryOptions: UseQueryOptions<
    Dictionary<Field>,
    unknown,
    Dictionary<Field>,
    typeof FINANCIAL_FIELDS_QUERY_KEY
  > = {},
): UseQueryResult<Dictionary<Field>> => {
  return useQuery(
    FINANCIAL_FIELDS_QUERY_KEY,
    async () => {
      const res = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Financial,
      );
      const fields = res?.fields || [];

      if (fields.length === 0) {
        return {};
      }

      return keyBy(fields, 'fieldName');
    },
    {
      staleTime: oneDay,
      ...queryOptions,
    },
  );
};

export const useFinancialFieldsV2 = () => {
  const query = useQuery(
    [FINANCIAL_FIELDS_QUERY_KEY, 'paginated'],
    async () => {
      const resp = await FieldsAPI.getPaginatedFields(
        ObjectType.ProjectFinanicalField,
        {
          offset: 0,
          limit: 50,
          includedFieldNames: [
            'costRate',
            'projectCustomBillRate',
            'projectRateCardUpdate',
            'projectBudget',
            'projectHours',
            'projectProfitability',
          ],
        },
      );

      return keyBy(resp?.data, 'fieldName');
    },
  );

  return query;
};

export const useBudgetFinancialFieldsQuery = () => {
  return useAllFieldsBaseQuery({
    staleTime: oneDay,
    select: (res) => {
      return res.filter(
        ({ objectType }) => objectType === FieldSectionType.Budget,
      );
    },
  });
};

// export const useBudgetFinancialFieldsQuery = () => {
//   return useQuery({
//     queryKey: FINANCIAL_BUDGET_FIELDS_QUERY_KEY,
//     queryFn: async () => {
//       return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Budget);
//     },
//     staleTime: oneDay,
//     select: (data) => data?.fields ?? [],
//   });
// };

export const useSprintFieldsQuery = (queryOptions = {}) =>
  useAllFieldsBaseQuery({
    cacheTime: 0,
    select: (res) => {
      return res.filter(
        ({ objectType }) => objectType === FieldSectionType.Sprint,
      );
    },
    ...queryOptions,
  });

export const useEpicsQuery = (
  options: UseQueryOptions<
    Epic[],
    unknown,
    Epic[],
    typeof ALL_EPICS_QUERY_KEY
  > = {},
): UseQueryResult<Epic[]> => {
  const { isDependentEntitiesLoading } = useHideNonStrategicPartner();

  return useQuery(ALL_EPICS_QUERY_KEY, () => SprintPlanningAPI.getEpicsList(), {
    enabled: !isDependentEntitiesLoading,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  });
};

// export const useEpicFieldsQuery = (queryOptions = {}) =>
//   useAllFieldsBaseQuery({
//     cacheTime: 0,
//     select: (res) => {
//       return res.filter(
//         ({ objectType }) => objectType === FieldSectionType.Epic,
//       );
//     },
//     ...queryOptions,
//   });

export const useEpicFieldsQuery = (queryOptions = {}) => {
  return useQuery({
    queryKey: EPIC_FIELDS_QUERY_KEY,
    queryFn: async () => {
      const epicFields = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Epic,
      );

      return epicFields?.fields || [];
    },
    cacheTime: 0,
    ...queryOptions,
  });
};

export const useFieldMutation = (
  mutationFn: MutationFunction<Field, FieldPayload>,
  options: UseMutationOptions<Field, unknown, FieldPayload> = {},
): UseMutationResult<Field, unknown, FieldPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Field, unknown, FieldPayload, unknown>(mutationFn, {
    ...options,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries([SETTINGS_SECTION_FIELDS_QUERY_KEY]);
      await queryClient.invalidateQueries(ALL_FIELDS_QUERY_KEY);
      await queryClient.invalidateQueries(fieldsQueryKeys[data.objectType]);
      if (data && data.objectType === FieldSectionType.Task) {
        await queryClient.invalidateQueries(TASK_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data && data.objectType === FieldSectionType.Company) {
        await queryClient.invalidateQueries(COMPANY_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data?.objectType === FieldSectionType.ResourceAllocation) {
        await queryClient.invalidateQueries(
          ALLOCATION_FIELD_SECTIONS_QUERY_KEY,
        );
      }

      if (data?.objectType === FieldSectionType.Project) {
        await queryClient.invalidateQueries(PROJECT_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data.objectType === FieldSectionType.Sprint) {
        await queryClient.invalidateQueries(SPRINT_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data.objectType === FieldSectionType.Epic) {
        await queryClient.invalidateQueries(EPIC_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data.objectType === FieldSectionType.Invoice) {
        await queryClient.invalidateQueries(INVOICE_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data.objectType === FieldSectionType.Budget) {
        await queryClient.invalidateQueries(
          BUDGET_FINANCIAL_FIELD_SECTIONS_QUERY_KEY,
        );
      }

      if (data.objectType === FieldSectionType.TimeEntry) {
        await queryClient.invalidateQueries(
          TIME_ENTRY_FIELD_SECTIONS_QUERY_KEY,
        );
      }

      if (data.objectType === FieldSectionType.Users) {
        if (data.objectSubType === FieldSectionSubType.Skill) {
          await queryClient.invalidateQueries(SKILL_FIELD_SECTIONS_QUERY_KEY);
          await queryClient.invalidateQueries([
            SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY,
          ]);
          await queryClient.refetchQueries([
            SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY,
          ]);
          await queryClient.refetchQueries([['skill-fields-for-read'], null]);
          await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
        } else if (data.objectSubType === FieldSectionSubType.ExternalUser) {
          await queryClient.invalidateQueries(
            CUSTOMER_TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY,
          );
        } else {
          await queryClient.invalidateQueries(
            TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY,
          );
        }
      }

      if (options.onSuccess) {
        await options.onSuccess(data, variables, context);
      }
    },
  });
};

export const GET_FIELD_BY_ID_QUERY_KEY = (fieldId: number) => [
  'GET_FIELD_BY_ID_QUERY',
  fieldId,
];

export const useGetFieldByIdQuery = (
  fieldId: number,
  queryOptions: UseQueryOptions<
    Field,
    unknown,
    Field,
    ReturnType<typeof GET_FIELD_BY_ID_QUERY_KEY>
  > = {},
): UseQueryResult<Field, unknown> =>
  useQuery(
    GET_FIELD_BY_ID_QUERY_KEY(fieldId),
    () => FieldsAPI.getField(fieldId),
    {
      staleTime: oneDay,
      ...queryOptions,
    },
  );

export const useCreateFieldMutation = (
  options: UseMutationOptions<Field, unknown, FieldPayload> = {},
) => {
  return useFieldMutation(
    (payload: FieldPayload) => FieldsAPI.createField(payload),
    options,
  );
};

export const useUpdateFieldMutation = (
  fieldId: number,
  options: UseMutationOptions<Field, unknown, FieldPayload> = {},
) => {
  const queryClient = useQueryClient();

  return useFieldMutation(
    (payload: FieldPayload) => FieldsAPI.updateField(fieldId, payload),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        options.onSuccess?.(data, variables, context);
        // Todo @Muskaan - Remove below query once pagination goes out
        queryClient.invalidateQueries(ALL_PROJECTS_RM_QUERY_KEY);
        queryClient.invalidateQueries(ALLOCATION_FIELDS_QUERY_KEY);
      },
    },
  );
};

export const useDeleteFieldMutation = (fieldId: number) => {
  return useFieldMutation(() => FieldsAPI.deleteField(fieldId));
};

export const useGetFieldProjectMapping = (fieldId) => {
  return useMutation<Field, unknown, FieldPayload, unknown>(
    ({ value }) => FieldsAPI.getFieldProjectMapping(fieldId, value),
    {
      onSuccess: async (data) => {
        return data;
      },
    },
  );
};

const ACCOUNT_META_QUERY_KEY = ['account-meta'];

export const useAccountMetaQuery = (
  queryOptions: UseQueryOptions<
    AccountMeta,
    unknown,
    AccountMeta,
    typeof ACCOUNT_META_QUERY_KEY
  > = {},
): UseQueryResult<AccountMeta> => {
  const pathname = useLocationPathname();
  const search = useLocationSearch();
  const searchParams = new URLSearchParams(search);
  const domain = searchParams.get('domain');

  return useQuery(
    ACCOUNT_META_QUERY_KEY,
    () => AccountAPI.getMeta(domain, pathname),
    {
      staleTime: oneDay,
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );
};

export const useCompanyUpdateMutation = (
  companyId: number,
): UseMutationResult<Company, unknown, UpdateCompanyPayload> => {
  return useMutation<Company, unknown, UpdateCompanyPayload>((payload) =>
    CompanyAPI.updateCompany(companyId, payload),
  );
};

export const PROJECT_TEMPLATE_QUERY_KEY = ['project_templates'];

export const useProjectTemplates = (
  options: UseQueryOptions<
    ProjectTemplateApi[],
    unknown,
    ProjectTemplateApi[],
    typeof PROJECT_TEMPLATE_QUERY_KEY
  > = {},
): UseQueryResult<ProjectTemplateApi[]> => {
  const isCurrentUserPartner = useIsCurrentUserPartner();

  const queryResult = useQuery(
    PROJECT_TEMPLATE_QUERY_KEY,
    () => {
      return ProjectTemplatesAPI.getAllTemplates();
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: !isCurrentUserPartner,
      ...options,
    },
  );

  // Combine both loading states into a single boolean
  const isLoadingState = !isCurrentUserPartner
    ? queryResult.isLoading
    : queryResult.isInitialLoading;

  // Return the original query result with the additional isLoadingState
  return {
    ...queryResult,
    isLoading: isLoadingState as any,
  };
};

const GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY = ['phases_project_templates'];

export const useGetPhasesProjectTemplatesQuery = (
  options: UseQueryOptions<
    ProjectPhaseOption[],
    unknown,
    ProjectPhaseOption[],
    typeof GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY
  > = {},
): UseQueryResult<ProjectPhaseOption[]> => {
  return useQuery(
    GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY,
    () => {
      return ProjectTemplatesAPI.getPhases();
    },
    { staleTime: fiveMinutes, refetchOnWindowFocus: false, ...options },
  );
};

export const useCreateCompanyMutation = (
  options: UseMutationOptions<Company, unknown, CreateCompanyPayload> = {},
) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<Company, unknown, CreateCompanyPayload>(
    (payload) => CompanyAPI.createCompany(payload),
    {
      onSuccess: async (company) => {
        dispatch(added(company));
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

export const useCreateUserMutation = (
  options: UseMutationOptions<User, unknown, UserCreationProps> = {},
): UseMutationResult<User, unknown, UserCreationProps> => {
  const queryClient = useQueryClient();

  return useMutation<User, unknown, UserCreationProps>(
    (payload) => UserAPI.createUser(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
      },
      ...options,
    },
  );
};

export const useAccountUpdateMutation = (
  options: UseMutationOptions<Account, unknown, UpdateAccountPayload> = {},
): UseMutationResult<Account, unknown, UpdateAccountPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Account, unknown, UpdateAccountPayload>(
    (payload) => AccountAPI.updateAccount(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

export const useOutOfOfficeUpdateMutation = (
  options: UseMutationOptions<
    OutOfOfficeConfig,
    unknown,
    OutOfOfficeConfig
  > = {},
): UseMutationResult<OutOfOfficeConfig, unknown, OutOfOfficeConfig> => {
  const queryClient = useQueryClient();

  return useMutation<OutOfOfficeConfig, unknown, OutOfOfficeConfig>(
    (payload) => AccountAPI.updateOutOfOffice(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

type ProjectCustomerQueryKey = [string, number, Record<string, unknown>];
const getProjectCustomerQueryKey = (
  [, projectId]: ProjectId,
  params = {},
): ProjectCustomerQueryKey => {
  return ['project', projectId, params];
};

export const useProjectCustomerQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<
    Project,
    unknown,
    Project,
    ProjectCustomerQueryKey
  > = {},
) => {
  const params = { include: 'customerId' };

  return useQuery(
    getProjectCustomerQueryKey(projectId, params),
    () => ProjectAPI.getProjectDetails(projectId[1], params),
    { staleTime: oneHour, ...options },
  );
};

export const useBulkUserCreateMutation = (
  options: UseMutationOptions<User[], unknown, BulkUserCreatePayload> = {},
) => {
  const queryClient = useQueryClient();

  return useMutation<User[], unknown, BulkUserCreatePayload>(
    (payload) => UserAPI.bulkCreate(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
      },
      ...options,
    },
  );
};

export const useAvatarUploadMutation = (
  options: UseMutationOptions<AvatarResponse, unknown, File> = {},
): UseMutationResult<AvatarResponse, unknown, File> => {
  const { user } = useUser();

  return useMutation<AvatarResponse, unknown, File>(
    (payload) => UserAPI.uploadProfilePic(payload),
    {
      ...options,
      onSuccess: (...args) => {
        if (user?.userId) {
          invalidateSwrUser(user.userId);
        }
        options.onSuccess?.(...args);
      },
    },
  );
};

export const useCompanyLogoUploadMutation = (
  options: UseMutationOptions<AvatarResponse, unknown, File> = {},
): UseMutationResult<AvatarResponse, unknown, File> => {
  return useMutation<AvatarResponse, unknown, File>(
    (payload) => CompanyAPI.uploadPicture(payload),
    options,
  );
};

type CompanyAddressQueryKey = [string, number, string];
export const getCompanyAddressQueryKey = (
  companyId: number,
  source: 'ACCOUNT' | 'INVOICE',
): CompanyAddressQueryKey => {
  return ['company-address', companyId, source];
};
export const getSpecificCompanyAddressQueryKey = (
  companyId: number,
  companyAddressId: number,
  source: 'ACCOUNT' | 'INVOICE',
): [string, number, number, string] => {
  return ['company-address-id', companyId, companyAddressId, source];
};

export const useCompanyAddressQuery = (
  companyId: number,
  source: 'ACCOUNT' | 'INVOICE' = 'ACCOUNT',
  options: UseQueryOptions<
    CompanyAddress[],
    unknown,
    CompanyAddress[],
    CompanyAddressQueryKey
  > = {},
) => {
  return useQuery(
    getCompanyAddressQueryKey(companyId, source),
    () => CompanyAPI.getCompanyAddresses(companyId, source),
    { staleTime: GENERAL_API_STALE, ...options },
  );
};

export const useSpecificCompanyAddressQuery = (
  companyId: number,
  companyAddressId: number,
  source: 'ACCOUNT' | 'INVOICE' = 'ACCOUNT',
  options: UseQueryOptions<
    CompanyAddress,
    unknown,
    CompanyAddress,
    [string, number, number, string]
  > = {},
) => {
  return useQuery(
    getSpecificCompanyAddressQueryKey(companyId, companyAddressId, source),
    () =>
      CompanyAPI.getSpecificCompanyAddress(companyId, companyAddressId, source),
    { staleTime: GENERAL_API_STALE, ...options },
  );
};

export const useUpdateCompanyAddressMutation = (): UseMutationResult<
  void,
  unknown,
  any
> => {
  return useMutation(({ companyId, addressId, payload }) =>
    CompanyAPI.editAddress(companyId, addressId, payload),
  );
};

export const useCreateCompanyAddressMutation = (): UseMutationResult<
  CompanyAddress,
  unknown,
  any
> => {
  return useMutation(({ companyId, payload }) =>
    CompanyAPI.createAddress(companyId, payload),
  );
};

export const useUpdateUserMetaMutation = (
  options: UseMutationOptions<UserProfileInfo, unknown, any> = {},
): UseMutationResult<UserProfileInfo, unknown, any> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<UserProfileInfo, unknown, any>(
    (payload) => UserAPI.updateMeta(payload),
    {
      onSuccess: async () => {
        /**
         * Since context provider is removed as part of perf optimization
         * https://github.com/Rocketlane/rocket-frontend/pull/6299
         * query key is not active, hence doing the following changes,
         * along with query invalidation.
         */
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        const accountMeta: AccountInitialData | undefined =
          queryClient.getQueryData(ACCOUNT_META_DATA_QUERY_KEY);

        if (accountMeta?.userProfile) {
          dispatch(success(accountMeta.userProfile));
        }
      },
      ...options,
    },
  );
};

export const useOptimisticUpdateUserMetaMutation = (
  options: UseMutationOptions<
    UserProfileInfo,
    unknown,
    any,
    UserProfileInfo
  > = {},
): UseMutationResult<UserProfileInfo, unknown, any> => {
  const dispatch = useDispatch();
  const usersData = useUser();

  return useMutation((payload) => UserAPI.updateMeta(payload), {
    onMutate: (payload) => {
      if (usersData?.userMeta) {
        const data = dotPropImmutable.set(
          usersData,
          'userMeta.metaData',
          payload.metaData,
        );

        dispatch(success(data));
      }

      return usersData;
    },
    onError: (error, _, previousData) => {
      if (previousData) {
        dispatch(success(previousData));
      }
    },
    ...options,
  });
};
/**
 * Note: remove this mutation (useUpdateCurrentUserMutation) and do not use it.
 * */
export const useUpdateCurrentUserMutation = (
  options: UseMutationOptions<User, unknown, Partial<User>> = {},
): UseMutationResult<User, unknown, Partial<User>> => {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation<User, unknown, Partial<User>>(
    (payload) => UserAPI.updateCurrentUser(payload),
    {
      ...options,
      onSuccess: async (...args) => {
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        if (user?.userId) {
          invalidateSwrUser(user.userId);
        }
        await options.onSuccess?.(...args);
      },
    },
  );
};

type UseCompanyLogoQueryParams = {
  companyDomain: string;
};

export const useCompanyLogoQuery = (
  params: UseCompanyLogoQueryParams,
): UseQueryResult<CompanyLogoResponse> => {
  return useQuery<
    CompanyLogoResponse,
    unknown,
    CompanyLogoResponse,
    [string, UseCompanyLogoQueryParams]
  >(
    ['company-domain', params],
    ({ queryKey }) => CompanyAPI.getCompanyLogoUrl(queryKey[1].companyDomain),
    {
      enabled: params.companyDomain.length > 0,
      staleTime: tenMinutes,
      refetchOnWindowFocus: false,
    },
  );
};

export const useCsatQuery = (companyId?: number | undefined) => {
  const userData = useUserSafe();

  return useQuery(['CSAT'], () => HomeAPI.getCsat(companyId), {
    enabled: [
      UserType.External,
      UserType.CustomerPortalBot,
      UserType.CustomerPartner,
    ].includes(userData?.user?.userType ?? (UserType.External as UserType)),
    staleTime: fiveMinutes,
  });
};

export const useProjectsCountQuery = (
  filter: GroupedFilter,
  user: UserProfileInfo,
  enabled: boolean,
) => {
  return useQuery(
    ['projects-count'],
    async () => {
      const { KanbanApi } = await import('api/tasks/kanban');

      return KanbanApi.getProjectsCountByUser({ filter });
    },
    {
      onSuccess: async (data) => {
        const projects: {
          [key: number]: { customer: Company; projectName: string };
        } = user.projects.reduce((obj, nextItem) => {
          obj[nextItem.projectId] = {
            customer: nextItem.customer,
            projectName: nextItem.projectName,
          };

          return obj;
        }, {});

        const result = data.reduce((acc, project) => {
          const { projectId, projectName: _projectName, count } = project;
          const projectName = _projectName || projects[projectId]?.projectName;
          const companyName = projects[projectId]?.customer?.companyName;
          const companyLogoUrl = projects[projectId]?.customer?.companyLogoUrl;

          acc[projectId] = {
            projectId,
            projectName,
            count,
            companyName,
            companyLogoUrl,
          };

          return acc;
        }, {});

        return result;
      },
      enabled,
    },
  );
};

export const useProjectMergeMutation = (
  projectId: ProjectId,
  options: UseMutationOptions<Project, unknown, ProjectMergePayload> = {},
): UseMutationResult<Project, unknown, ProjectMergePayload> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, ProjectMergePayload>(
    (payload) => ProjectAPI.merge(projectId[1], payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(projectId),
        );
        await queryClient.invalidateQueries(getProjectTasksQueryKey(projectId));
      },
      ...options,
    },
  );
};

export const getDocumentsQueryKey = ([, projectId]: ProjectId) => [
  'documents',
  projectId,
];
export const useDocumentsQuery = (projectId: ProjectId) => {
  return useQuery(
    getDocumentsQueryKey(projectId),
    () => DocumentsAPI.getDocumentsList(projectId[1]),
    {
      staleTime: tenMinutes,
    },
  );
};

export const useGetDocumentForSpaceTab = (
  projectId: number,
  spaceId: number,
) => {
  const { data = [] } = useDocumentsQuery(['project_id', projectId]);

  const doc = useMemo(() => {
    return data.find(
      (d) =>
        d.source?.sourceId === spaceId &&
        d?.source?.sourceType === DocumentSourceType.SpaceTab,
    );
  }, [data, spaceId]);

  return doc;
};

export const useAddExistingTasksMutation = (
  projectId: number,
  options: UseMutationOptions<
    Task[],
    unknown,
    { tasks: Task[]; meta: any }
  > = {},
): UseMutationResult<Task[], unknown, { tasks: Task[]; meta: any }> => {
  return useMutation(
    ({ tasks, meta }) =>
      DocumentsAPI.addExistingSectionTask(projectId, tasks, meta),
    {
      ...options,
    },
  );
};

export type UseAddMembersToProjectMutationVariables = {
  projectId: ProjectId;
  payload: InvitedMembers[];
  suppressToast?: boolean;
};

export const useAddMembersToProjectMutation = (): UseMutationResult<
  InvitedMembers[],
  unknown,
  UseAddMembersToProjectMutationVariables
> => {
  const successTitle = useTranslation('invitation.team_member.success.title');
  const successMessage = useTranslation(
    'invitation.team_member.success.content',
  );
  const errorTitle = useTranslation('invitation.team_member.error.title');
  const errorMessage = useTranslation('invitation.team_member.error.content');
  const queryClient = useQueryClient();

  return useMutation<
    InvitedMembers[],
    unknown,
    UseAddMembersToProjectMutationVariables
  >(({ projectId, payload }) => ProjectAPI.addMembers(projectId[1], payload), {
    onSuccess: async (_, { projectId, suppressToast }) => {
      await queryClient.invalidateQueries(getProjectMembersQueryKey(projectId));
      await queryClient.invalidateQueries(getProjectUsersLiteKey(projectId[1]));
      // Invalidate budget exclusions queries for the project
      await queryClient.invalidateQueries([
        'project-members-exclusion',
        projectId,
      ]);
      await queryClient.invalidateQueries(['budget-member-exclusion']);
      await queryClient.invalidateQueries(MEMBERS_V2_QUERY_KEY);
      await queryClient.invalidateQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
      if (suppressToast !== true) {
        toastService({
          title: successTitle,
          message: successMessage,
        }).success();
      }
    },
    onError: (_, { suppressToast }) => {
      if (suppressToast !== true) {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();
      }
    },
  });
};

export const GET_EMAIL_CONFIG = ['getEmailConfig'];

export const useGetDKIMConfig = (): UseQueryResult<EmailConfig[], unknown> => {
  return useQuery(GET_EMAIL_CONFIG, () => AccountAPI.getEmailConfig(), {
    retry: 1,
    staleTime: fiveMinutes,
  });
};

export const useFTUEMutation = (): UseMutationResult<
  unknown,
  unknown,
  FtuePayload
> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, FtuePayload>({
    mutationFn: (payload) => AccountAPI.toggleFTUE(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      // Todo @Muskaan - Remove below query once pagination goes out
      await queryClient.invalidateQueries(ALL_PROJECTS_RM_QUERY_KEY);
      await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
    },
  });
};

export const useCreateTaskMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<Task, unknown, any> => {
  const queryClient = useQueryClient();

  return useMutation<Task, unknown, any>(
    (payload) => TaskAPI.createTask(projectId, payload),
    {
      onSuccess: async (data) => {
        if (data.projectPhase) {
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(ProjectId(projectId)),
          );
          await queryClient.refetchQueries(
            getProjectTasksQueryKey(ProjectId(projectId)),
          );
        }
      },
    },
  );
};

export const useAddTaskTemplateMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<Task[], unknown, any> => {
  const queryClient = useQueryClient();

  return useMutation<Task[], unknown, any>(
    (payload) => TaskAPI.addTaskTemplatesToProject(projectId, payload),
    {
      onSuccess: async (data) => {
        await queryClient.refetchQueries(
          getProjectTasksQueryKey(['project_id', projectId]),
        );
        if (data[0]?.projectPhase) {
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(ProjectId(projectId)),
          );
        }
      },
    },
  );
};

export const useDuplicateTaskMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<void, unknown, DuplicateTaskProps> => {
  const duplicationInProgress = useTranslation(
    'task.duplicate_task.in_progress',
  );
  const notifyDuplication = useTranslation('task.duplicate_task.notify');
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DuplicateTaskProps>(
    (payload) => TaskAPI.duplicateTask(projectId, payload),
    {
      onSuccess: async () => {
        toastService({
          title: duplicationInProgress,
          message: notifyDuplication,
        }).success();
        queryClient.refetchQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );

        await queryClient.invalidateQueries(
          getProjectDependencyMapKey(projectId),
        );
      },
    },
  );
};

export const useCreateProjectTaskMutation = (): UseMutationResult<
  Task,
  unknown,
  any
> => {
  return useMutation<
    Task,
    unknown,
    { projectId: number; payload: { taskName: string } }
  >(({ projectId, payload }) => TaskAPI.createTask(projectId, payload));
};

/**
 * Custom domain queries
 */

export const GET_CUSTOM_DOMAIN_QUERY_KEY = ['getCustomDomainQuery'];

export const useCustomDomainConfigQuery = (): UseQueryResult<
  CustomDomain[],
  unknown
> => {
  return useQuery(
    GET_CUSTOM_DOMAIN_QUERY_KEY,
    () => AccountAPI.getCustomDomain(),
    {
      retry: 1,
      staleTime: fiveMinutes,
      refetchOnMount: true,
    },
  );
};

export const useUpdateEntityOrderOfPhaseMutation = (
  phaseId: number,
): UseMutationResult<null, unknown, UpdateEntityOrderPayload> => {
  return useMutation<null, unknown, UpdateEntityOrderPayload>((payload) =>
    PhaseAPI.updateEntityOrder(phaseId, payload),
  );
};

export const useGetProjectPhaseQuery = (
  phaseId: ProjectPhaseId,
): UseQueryResult<ProjectPhase> => {
  return useQuery(getProjectPhaseQueryKey(phaseId), () =>
    PhaseAPI.getProjectPhase(phaseId[1]),
  );
};

export const useUpdateSubTaskOrderMutation = (
  taskId: number,
): UseMutationResult<void, unknown, UpdateSubTaskOrderPayload> => {
  return useMutation<void, unknown, UpdateSubTaskOrderPayload>((payload) =>
    TaskAPI.updateSubTaskOrder(taskId, payload),
  );
};

export const useProjectMembersMetaQuery = (
  [, projectId]: ProjectId,
  options: UseQueryOptions<
    MembersMeta,
    unknown,
    MembersMeta,
    [string, number]
  > = {},
): UseQueryResult<MembersMeta> => {
  return useQuery(
    ['members-meta', projectId],
    () => ProjectAPI.getProjectMembersMeta(projectId),
    options,
  );
};

// @TODO commented as part of removing get-members api. Remove this hook after confirming that there is no impact
/* export const useAllProjectMembershipsQuery = (): UseQueryResult<
  ProjectMembership[]
> => {
  const dispatch = useDispatch();

  return useQuery<ProjectMembership[]>(
    'ALL_PROJECT_MEMBERSHIPS',
    () => MembersAPI.getAllProjectMemberships(),
    {
      onSuccess: (memberships) => {
        dispatch(
          setLoadedSeveral(
            memberships.map(({ projectId, members, teamList }) => ({
              projectId: ProjectId(projectId),
              memberships: members.map(
                ({
                  user: { userId },
                  permissionSet: { permissionSetType: memberType },
                  joinedAt,
                }) => ({
                  userId: UserId(userId),
                  memberType,
                  joinedAt,
                }),
              ),
              placeholders: teamList,
            })),
          ),
        );
      },
    },
  );
}; */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      cacheTime: 0,
      staleTime: 0,
      retry: 1,
    },
  },
});
